/**
 * Rule Configuration screen (item 9/10, Asana 1216373781992284, spec §10.1, §7.2-7.3).
 *
 * The biggest unbuilt surface in the framework: the client-tunes-without-a-developer promise
 * (our differentiation). This screen edits a DRAFT of sparse overrides on top of
 * defaultRuleset.js — factor weights, gate enable/priority, box residual risk + target-platform
 * catalog, and the real numeric thresholds — then lets the consultant preview the exact impact
 * ("publishing this draft re-scores N EUCs; M change box") before proposing it for TSG review.
 *
 * Item 10's governance workflow sits on top of item 9's draft/publish: Propose moves a draft to
 * pending_review (locked from further edits — the backend rejects PUT /rule-config/draft while
 * pending, surfaced here by disabling the controls); Publish is gated server-side to the ADMIN
 * role (the actual TSG reviewer) AND requires pending_review, so the button is shown to everyone
 * but will 403/409 for a non-reviewer or an un-proposed draft — the frontend hint (useAuth's role)
 * is UX only, the backend is the real boundary. Every publish also writes an immutable version
 * history row, shown in the panel at the bottom of this screen.
 *
 * What's editable here is real and wired straight into dispositionEngine.js via
 * ruleConfig.mergeRulesetOverrides(). What's NOT editable is shown read-only with an honest note
 * rather than a control that would silently do nothing:
 *   - Gate CONDITION logic — gates are JS predicates, not a string DSL (defaultRuleset.js's own
 *     comment says the DSL is future work for this exact screen). enabled + priority are real.
 *   - Timing triggers — computeTiming() is hardcoded JS branching, not ruleset-driven yet.
 *
 * Origin badges are 3-state: DEFAULT (untouched), PROPOSED (in the local draft, not yet reviewed),
 * NEGOTIATED (present in the currently PUBLISHED overrides — i.e. it already went through a TSG
 * review and was published). "Negotiated" is a real signal, not a guess: it's derived from whether
 * the field is in rc.published, which only ever contains what an ADMIN actually approved.
 *
 * The impact preview is the highest-value affordance on the screen (Ryan, item 9 note: "it's what
 * makes tuning safe — don't ship the sliders without it") — Publish is disabled until a fresh
 * preview has been run against the current draft.
 */

import { useState } from 'react';
import { useRuleConfig, MATERIALITY_BANDS } from './useRuleConfig';
import type { RuleBaseGate, RulesetVersionDetail } from './useRuleConfig';
import { useFrameworkConfig } from './useFrameworkConfig';
import { useAuth } from '../../../contexts/AuthContext';
import { color, font } from '../tokens';

const C = {
  accent: color.accent, deep: color.navy, ink: '#1e1b3a',
  body: '#4a4966', subtle: color.textMuted, label: '#7c7a95', border: color.cardBorder, soft: '#f9fafb', surface: color.surface,
};
const mono = 'JetBrains Mono, monospace';

type Origin = 'default' | 'proposed' | 'negotiated';
const ORIGIN_STYLE: Record<Origin, { bg: string; fg: string; label: string }> = {
  default:    { bg: '#f1f0f6', fg: C.label,  label: 'DEFAULT' },
  proposed:   { bg: '#fef3c7', fg: '#9a5b00', label: 'PROPOSED' },   // in the local draft, not yet reviewed
  negotiated: { bg: '#ede9fe', fg: C.accent, label: 'NEGOTIATED' },  // in rc.published — already TSG-reviewed
};

function Badge({ origin }: { origin: Origin }) {
  const s = ORIGIN_STYLE[origin];
  return (
    <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', padding: '2px 7px', borderRadius: 999, background: s.bg, color: s.fg }}>
      {s.label}
    </span>
  );
}

/** A field is 'proposed' if the local draft touches it (pending TSG review), 'negotiated' if it's
 *  absent from the draft but present in the currently PUBLISHED overrides (already reviewed and
 *  live), otherwise 'default'. */
function originFor(localBucket: Record<string, any> | undefined, publishedBucket: Record<string, any> | undefined, key: string): Origin {
  if (localBucket && localBucket[key] != null) return 'proposed';
  if (publishedBucket && publishedBucket[key] != null) return 'negotiated';
  return 'default';
}

function SectionCard({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, padding: 18, marginBottom: 18 }}>
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: C.ink }}>{title}</div>
        {subtitle && <div style={{ fontSize: 12, color: C.subtle, marginTop: 3 }}>{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}

function LimitationNote({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontSize: 11, color: '#9a5b00', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 10, padding: '8px 12px', marginTop: 10, lineHeight: 1.5 }}>
      {children}
    </div>
  );
}

export default function RuleConfiguration() {
  const rc = useRuleConfig();
  const { targets, customTargets, setTarget, addCustomTarget } = useFrameworkConfig();
  const [publishConfirm, setPublishConfirm] = useState(false);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [versionDetail, setVersionDetail] = useState<RulesetVersionDetail | null>(null);
  const [versionDetailLoading, setVersionDetailLoading] = useState<number | null>(null);
  // UX hint only — the backend's ADMIN role check on POST /rule-config/publish is the real
  // boundary. AuthContext's `role` type is stale against the backend's actual enum
  // (READ_ONLY/CONSULTANT/ADMIN/SUPER_ADMIN), so this reads the runtime value loosely rather than
  // trusting the TS type.
  const { user } = useAuth();
  const canReview = ['ADMIN', 'SUPER_ADMIN', 'MASTER'].includes((user as any)?.role || '');

  if (!rc.loaded) {
    return <div style={{ fontFamily: font.ui, color: C.subtle, padding: 40, textAlign: 'center' }}>Loading rule configuration…</div>;
  }
  if (!rc.base) {
    return (
      <div style={{ fontFamily: font.ui, color: C.body, maxWidth: 560, margin: '48px auto', textAlign: 'center' }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#991b1b' }}>Couldn't load the rule configuration</div>
        <div style={{ fontSize: 12.5, color: C.subtle, marginTop: 6 }}>{rc.error || 'Unknown error'}</div>
      </div>
    );
  }

  const dimensions = Array.from(new Set(rc.base.factors.map((f) => f.dimension)));
  const gatesByPriority = [...rc.base.gates].sort((a, b) => {
    const pa = rc.local.gates?.[a.id]?.priority ?? a.priority;
    const pb = rc.local.gates?.[b.id]?.priority ?? b.priority;
    return pa - pb;
  });

  const changeCount = countOverrides(rc.local);

  const doPublish = async () => {
    if (!publishConfirm) { setPublishConfirm(true); return; }
    const r = await rc.publish();
    setPublishConfirm(false);
    if (!r.ok) window.alert(r.error || 'Publish failed');
  };

  const doPropose = async () => {
    const r = await rc.propose();
    if (!r.ok) window.alert(r.error || 'Propose failed');
  };
  const doWithdraw = async () => {
    const r = await rc.withdraw();
    if (!r.ok) window.alert(r.error || 'Withdraw failed');
  };
  const toggleVersions = async () => {
    setVersionsOpen((v) => !v);
    if (!rc.versionsLoaded) await rc.loadVersions();
  };
  const openVersionDetail = async (version: number) => {
    setVersionDetailLoading(version);
    const d = await rc.loadVersionDetail(version);
    setVersionDetail(d);
    setVersionDetailLoading(null);
  };

  return (
    <div style={{ fontFamily: font.ui, color: C.body }}>
      {/* Hero + version bar */}
      <div style={{ marginBottom: 22 }}>
        <div style={{ fontFamily: font.ui, fontSize: 11, letterSpacing: '0.12em', color: C.label, textTransform: 'uppercase' }}>
          Disposition Framework · Rule Configuration (spec §10.1)
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 16, marginTop: 8, flexWrap: 'wrap' }}>
          <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: C.ink, letterSpacing: '-0.01em' }}>
            Tune the framework without a developer.
          </h1>
        </div>
        <div style={{ fontSize: 13.5, color: C.subtle, marginTop: 4 }}>
          Edit factor weights, gate toggles, box definitions, and thresholds below. Nothing here changes real dispositions until you Save Draft, preview the impact, and Publish.
        </div>
      </div>

      {rc.error && (
        <div style={{ fontSize: 12.5, color: '#991b1b', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '10px 14px', marginBottom: 16 }}>
          {rc.error}
        </div>
      )}

      {/* Version bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12,
        background: C.ink, color: '#fff', borderRadius: 14, padding: '14px 18px', marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontFamily: font.ui, fontSize: 11, letterSpacing: '0.08em', color: '#a99cff', textTransform: 'uppercase' }}>Published</div>
            <div style={{ fontSize: 15, fontWeight: 800 }}>
              v{rc.publishedVersion}{rc.publishedAt ? ` · ${new Date(rc.publishedAt).toLocaleString()}` : ' · unpublished (defaults)'}
            </div>
          </div>
          <div style={{ width: 1, height: 28, background: 'rgba(255,255,255,.15)' }} />
          <div>
            <div style={{ fontFamily: font.ui, fontSize: 11, letterSpacing: '0.08em', color: '#a99cff', textTransform: 'uppercase' }}>Draft status</div>
            <div style={{ fontSize: 15, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 8 }}>
              {rc.locked ? 'Pending TSG review' : changeCount === 0 ? 'No changes' : `${changeCount} change${changeCount === 1 ? '' : 's'} pending`}
              {rc.dirty && !rc.locked && <span style={{ color: '#fbbf24', fontWeight: 700, fontSize: 12 }}>unsaved</span>}
              {rc.locked && rc.proposedBy && (
                <span style={{ fontSize: 11, fontWeight: 500, color: '#c9c2f5' }}>
                  — proposed by {rc.proposedBy.name || rc.proposedBy.email || `user #${rc.proposedBy.id}`}{rc.proposedAt ? ` · ${new Date(rc.proposedAt).toLocaleString()}` : ''}
                </span>
              )}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={toggleVersions} style={btnStyle(false, 'rgba(255,255,255,.1)', '#fff')}>
            {versionsOpen ? 'Hide' : 'Show'} History
          </button>
          {!rc.locked ? (
            <>
              <button onClick={rc.saveDraft} disabled={!rc.dirty || rc.saving} style={btnStyle(!rc.dirty || rc.saving, color.navy, '#fff')}>
                {rc.saving ? 'Saving…' : 'Save Draft'}
              </button>
              <button onClick={rc.runPreview} disabled={changeCount === 0 || rc.previewing} style={btnStyle(changeCount === 0 || rc.previewing, C.accent, '#fff')}>
                {rc.previewing ? 'Computing…' : 'Preview Impact'}
              </button>
              <button onClick={doPropose} disabled={changeCount === 0 || rc.dirty || rc.proposing}
                title={rc.dirty ? 'Save the draft before proposing it' : changeCount === 0 ? 'No changes to propose' : undefined}
                style={btnStyle(changeCount === 0 || rc.dirty || rc.proposing, '#d97706', '#fff')}>
                {rc.proposing ? 'Proposing…' : 'Propose for TSG Review'}
              </button>
            </>
          ) : (
            <>
              <button onClick={doWithdraw} disabled={rc.withdrawing} style={btnStyle(rc.withdrawing, 'rgba(255,255,255,.1)', '#fff')}>
                {rc.withdrawing ? 'Withdrawing…' : 'Withdraw Proposal'}
              </button>
              <button onClick={rc.runPreview} disabled={rc.previewing} style={btnStyle(rc.previewing, C.accent, '#fff')}>
                {rc.previewing ? 'Computing…' : 'Preview Impact'}
              </button>
              <button onClick={doPublish}
                disabled={!rc.preview || rc.previewStale || rc.publishing}
                title={!canReview ? 'Requires the ADMIN (TSG reviewer) role — will be rejected server-side otherwise' : rc.previewStale ? 'Run a fresh preview first — the draft changed since the last one' : !rc.preview ? 'Preview the impact before publishing' : undefined}
                style={btnStyle(!rc.preview || rc.previewStale || rc.publishing, publishConfirm ? '#991b1b' : canReview ? '#16a34a' : '#78350f', '#fff')}>
                {rc.publishing ? 'Publishing…' : publishConfirm ? 'Confirm Publish?' : canReview ? 'Publish' : 'Publish (needs TSG reviewer)'}
              </button>
            </>
          )}
        </div>
      </div>

      {rc.locked && (
        <div style={{ fontSize: 12, color: '#9a5b00', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 10, padding: '10px 14px', marginBottom: 18 }}>
          This draft is pending TSG review — the factor/gate/box/threshold controls below are locked. Withdraw the proposal to keep editing, or wait for an ADMIN reviewer to Publish or send it back.
        </div>
      )}

      {/* Impact preview */}
      {rc.preview && (
        <div style={{ background: rc.previewStale ? '#f1f0f6' : '#f5f3ff', border: `1px solid ${rc.previewStale ? C.border : '#d9cffc'}`, borderRadius: 14, padding: 16, marginBottom: 18, opacity: rc.previewStale ? 0.6 : 1 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: C.ink, marginBottom: 4 }}>
            {rc.preview.no_op
              ? 'This draft changes nothing versus the published ruleset.'
              : `Publishing this draft re-scores ${rc.preview.total} EUCs; ${rc.preview.changed} change box${rc.preview.timing_changed ? `, ${rc.preview.timing_changed} change timing` : ''}.`}
            {rc.previewStale && <span style={{ color: '#9a5b00', fontWeight: 700 }}> (stale — the draft changed since this preview)</span>}
          </div>
          {rc.preview.changes.length > 0 && (
            <div style={{ marginTop: 10, display: 'grid', gap: 4, maxHeight: 220, overflowY: 'auto' }}>
              {rc.preview.changes.map((c) => (
                <div key={c.euc_id} style={{ display: 'flex', gap: 10, fontSize: 12, fontFamily: mono, color: C.body, padding: '4px 8px', background: C.surface, borderRadius: 8 }}>
                  <span style={{ color: C.ink, fontWeight: 700, minWidth: 70 }}>{c.euc_id}</span>
                  <span style={{ color: C.subtle, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.file}</span>
                  <span>{c.from} → <b style={{ color: C.accent }}>{c.to}</b></span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Factor library */}
      <SectionCard title="Factor library" subtitle="24 factors across 7 dimensions. Weight range 0–2; 1.0 is neutral (the engine's default).">
        {dimensions.map((dim) => (
          <div key={dim} style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.label, letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 8 }}>{dim}</div>
            <div style={{ display: 'grid', gap: 8 }}>
              {rc.base!.factors.filter((f) => f.dimension === dim).map((f) => {
                const weight = rc.local.factors?.[f.id]?.weight ?? rc.published.factors?.[f.id]?.weight ?? f.default_weight;
                const origin = originFor(rc.local.factors, rc.published.factors, f.id);
                return (
                  <div key={f.id} style={{ display: 'grid', gridTemplateColumns: '52px 1fr 200px 40px', alignItems: 'center', gap: 12, padding: '6px 4px', opacity: rc.locked ? 0.6 : 1 }}>
                    <span style={{ fontFamily: mono, fontSize: 11, fontWeight: 700, color: C.subtle }}>{f.id}</span>
                    <span style={{ fontSize: 12.5, color: C.ink }}>{f.name}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input type="range" min={0} max={2} step={0.1} value={weight} disabled={rc.locked}
                        onChange={(e) => rc.setFactorWeight(f.id, Number(e.target.value))}
                        style={{ flex: 1, accentColor: C.accent }} />
                      <Badge origin={origin} />
                    </div>
                    <span style={{ fontFamily: mono, fontSize: 12, fontWeight: 700, color: origin !== 'default' ? C.accent : C.subtle, textAlign: 'right' }}>{weight.toFixed(1)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </SectionCard>

      {/* Gate editor */}
      <SectionCard title="Gate editor" subtitle="8 hard gates, evaluated in priority order (lowest first). Enable/priority are real; condition logic needs a developer.">
        <div style={{ display: 'grid', gap: 6 }}>
          {gatesByPriority.map((g) => (
            <GateRow key={g.id} gate={g}
              enabled={rc.local.gates?.[g.id]?.enabled ?? rc.published.gates?.[g.id]?.enabled ?? g.enabled}
              priority={rc.local.gates?.[g.id]?.priority ?? rc.published.gates?.[g.id]?.priority ?? g.priority}
              origin={originFor(rc.local.gates, rc.published.gates, g.id)}
              disabled={rc.locked}
              onToggle={(v) => rc.setGateEnabled(g.id, v)}
              onPriority={(v) => rc.setGatePriority(g.id, v)} />
          ))}
        </div>
        <LimitationNote>
          Condition logic (what makes a gate fire) is defined in code, not a string DSL yet — editing it needs a developer. Enable and priority are fully wired here.
        </LimitationNote>
      </SectionCard>

      {/* Box definitions */}
      <SectionCard title="Box definitions" subtitle="Residual risk and target-platform catalog per outcome box. Mandatory controls are transcribed from the spec and read-only.">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
          {rc.base.boxes.map((b) => {
            const risk = rc.local.boxes?.[b.id]?.residual_risk ?? rc.published.boxes?.[b.id]?.residual_risk ?? b.residual_risk;
            const origin = originFor(rc.local.boxes, rc.published.boxes, b.id);
            const options = Array.from(new Set([...b.target_platform_catalog, ...(customTargets[b.id] || [])]));
            return (
              <div key={b.id} style={{ border: `1px solid ${C.border}`, borderRadius: 14, padding: 14, background: C.soft, opacity: rc.locked ? 0.6 : 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <span style={{ fontSize: 12.5, fontWeight: 800, color: C.ink }}>{b.label}</span>
                  <Badge origin={origin} />
                </div>
                <label style={{ fontSize: 11, color: C.label, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Residual risk</label>
                <select value={risk} disabled={rc.locked} onChange={(e) => rc.setBoxResidualRisk(b.id, e.target.value as any)}
                  style={{ display: 'block', width: '100%', marginTop: 4, marginBottom: 10, fontFamily: mono, fontSize: 12, color: C.body,
                    background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: '6px 8px' }}>
                  {['LOW', 'MEDIUM', 'HIGH'].map((r) => <option key={r} value={r}>{r}</option>)}
                </select>

                <label style={{ fontSize: 11, color: C.label, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Target platform catalog</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6, marginBottom: 8 }}>
                  {options.map((p) => (
                    <span key={p} style={{ fontSize: 11, fontFamily: mono, color: p === (targets[b.id] || b.target_platform_catalog[0]) ? C.accent : C.subtle,
                      background: C.surface, border: `1px solid ${C.border}`, borderRadius: 999, padding: '3px 9px' }}>{p}</span>
                  ))}
                </div>
                {!rc.locked && <AddPlatformChip onAdd={(v) => addCustomTarget(b.id, v)} />}

                {b.mandatory_controls && b.mandatory_controls.length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 11, color: C.label, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Mandatory controls (read-only)</div>
                    <div style={{ display: 'grid', gap: 4 }}>
                      {b.mandatory_controls.map((mc) => (
                        <div key={mc} style={{ fontSize: 11, color: C.body, display: 'flex', gap: 6 }}>
                          <span style={{ color: '#16a34a' }}>✓</span>{mc}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </SectionCard>

      {/* Timing triggers — read-only */}
      <SectionCard title="Timing triggers" subtitle="Cat 1 / Cat 2 / Cat 3 timing rules, transcribed from the engine's computeTiming() logic.">
        <div style={{ display: 'grid', gap: 8, fontSize: 12, color: C.body }}>
          <TimingRow band="Cat 3 — Deferred" rule="No owner, unresolved dependencies, a required strategic decision, flagged for review, or model-risk (SR 11-7). Outranks everything else." />
          <TimingRow band="Cat 1 — Immediate" rule="A forcing event (regulatory clock / G7) is present, OR the EUC is a low-complexity Box 3 (platform migration) / Box 4 (retain) lift, OR a clean as-is lift that doesn't need a high-value redesign." />
          <TimingRow band="Cat 2 — Near-term" rule="Default for Box 1 / Box 2 modernizations that don't match a Cat 1 or Cat 3 rule above." />
        </div>
        <LimitationNote>
          Timing rules are hardcoded branching on EUC flags — nothing in the ruleset drives them yet, so there's no control here to bind to. Showing this read-only rather than a slider that wouldn't do anything.
        </LimitationNote>
      </SectionCard>

      {/* Thresholds */}
      <SectionCard title="Thresholds" subtitle="Numeric knobs the engine reads directly — gate cutoffs, confidence bands, and the consolidation overlap-signal weights.">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 14 }}>
          <ThresholdField label="Dead months (G2)" value={rc.local.thresholds?.dead_months ?? rc.published.thresholds?.dead_months ?? rc.base.thresholds.dead_months}
            origin={originFor(rc.local.thresholds, rc.published.thresholds, 'dead_months')} disabled={rc.locked} min={0} max={60} step={1}
            onChange={(v) => rc.setThreshold('dead_months', v)} />
          <ThresholdField label="Overlap epsilon (consolidation)" value={rc.local.thresholds?.overlap_epsilon ?? rc.published.thresholds?.overlap_epsilon ?? rc.base.thresholds.overlap_epsilon}
            origin={originFor(rc.local.thresholds, rc.published.thresholds, 'overlap_epsilon')} disabled={rc.locked} min={0} max={1} step={0.01}
            onChange={(v) => rc.setThreshold('overlap_epsilon', v)} />
          <ThresholdField label="Confidence — auto-confirm floor" value={rc.local.thresholds?.confidence_autoconfirm ?? rc.published.thresholds?.confidence_autoconfirm ?? rc.base.thresholds.confidence_autoconfirm}
            origin={originFor(rc.local.thresholds, rc.published.thresholds, 'confidence_autoconfirm')} disabled={rc.locked} min={0} max={1} step={0.01}
            onChange={(v) => rc.setThreshold('confidence_autoconfirm', v)} />
          <ThresholdField label="Confidence — review floor" value={rc.local.thresholds?.confidence_review_floor ?? rc.published.thresholds?.confidence_review_floor ?? rc.base.thresholds.confidence_review_floor}
            origin={originFor(rc.local.thresholds, rc.published.thresholds, 'confidence_review_floor')} disabled={rc.locked} min={0} max={1} step={0.01}
            onChange={(v) => rc.setThreshold('confidence_review_floor', v)} />
          <ThresholdField label="Tiebreak epsilon" value={rc.local.thresholds?.tiebreak_epsilon ?? rc.published.thresholds?.tiebreak_epsilon ?? rc.base.thresholds.tiebreak_epsilon}
            origin={originFor(rc.local.thresholds, rc.published.thresholds, 'tiebreak_epsilon')} disabled={rc.locked} min={0} max={1} step={0.01}
            onChange={(v) => rc.setThreshold('tiebreak_epsilon', v)} />
          <ThresholdField label="Near-miss margin" value={rc.local.thresholds?.overlap_near_miss_margin ?? rc.published.thresholds?.overlap_near_miss_margin ?? rc.base.thresholds.overlap_near_miss_margin}
            origin={originFor(rc.local.thresholds, rc.published.thresholds, 'overlap_near_miss_margin')} disabled={rc.locked} min={0} max={1} step={0.01}
            onChange={(v) => rc.setThreshold('overlap_near_miss_margin', v)} />
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 11, color: C.label, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Materiality gate band (G4 floor)</label>
          <select value={rc.local.thresholds?.materiality_gate_band ?? rc.published.thresholds?.materiality_gate_band ?? rc.base.thresholds.materiality_gate_band}
            disabled={rc.locked}
            onChange={(e) => rc.setMaterialityGateBand(e.target.value as any)}
            style={{ display: 'block', marginTop: 6, fontFamily: mono, fontSize: 12, color: C.body,
              background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: '6px 10px', minWidth: 160 }}>
            {MATERIALITY_BANDS.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>

        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.label, letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 8 }}>
            Consolidation overlap-signal weights
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
            {(['input_source', 'formula', 'output_schema'] as const).map((k) => {
              const v = rc.local.thresholds?.overlap_signal_weights?.[k] ?? rc.published.thresholds?.overlap_signal_weights?.[k] ?? rc.base!.thresholds.overlap_signal_weights[k];
              const origin = originFor(rc.local.thresholds?.overlap_signal_weights, rc.published.thresholds?.overlap_signal_weights, k);
              return (
                <ThresholdField key={k} label={k.replace('_', ' ')} value={v} origin={origin} disabled={rc.locked} min={0} max={1} step={0.01}
                  onChange={(nv) => rc.setOverlapSignalWeight(k, nv)} />
              );
            })}
          </div>
        </div>
      </SectionCard>

      {versionsOpen && (
        <VersionHistoryPanel versions={rc.versions} loaded={rc.versionsLoaded}
          detail={versionDetail} detailLoading={versionDetailLoading}
          onOpenDetail={openVersionDetail} onCloseDetail={() => setVersionDetail(null)} />
      )}
    </div>
  );
}

function countOverrides(o: ReturnType<typeof useRuleConfig>['local']): number {
  let n = 0;
  n += Object.keys(o.factors || {}).length;
  n += Object.keys(o.gates || {}).length;
  n += Object.keys(o.boxes || {}).length;
  const t = o.thresholds || {};
  n += Object.keys(t).filter((k) => k !== 'overlap_signal_weights').length;
  n += Object.keys(t.overlap_signal_weights || {}).length;
  return n;
}

function btnStyle(disabled: boolean, bg: string, fg: string): React.CSSProperties {
  return {
    cursor: disabled ? 'default' : 'pointer', fontSize: 12.5, fontWeight: 700, color: fg,
    background: disabled ? '#4a4770' : bg, opacity: disabled ? 0.5 : 1,
    border: 'none', borderRadius: 9, padding: '9px 16px',
  };
}

function GateRow({ gate, enabled, priority, origin, disabled, onToggle, onPriority }: {
  gate: RuleBaseGate; enabled: boolean; priority: number; origin: Origin; disabled?: boolean;
  onToggle: (v: boolean) => void; onPriority: (v: number) => void;
}) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr 90px 70px 44px', alignItems: 'center', gap: 12, padding: '8px 10px', background: C.soft, borderRadius: 10, opacity: disabled ? 0.6 : 1 }}>
      <span style={{ fontFamily: mono, fontSize: 11, fontWeight: 700, color: C.subtle }}>{gate.id}</span>
      <div>
        <div style={{ fontSize: 12.5, color: C.ink, fontWeight: 600 }}>{gate.name}</div>
        <div style={{ fontSize: 11, color: C.subtle, fontFamily: mono }}>{gate.action?.type}{gate.action?.reason ? ` — ${gate.action.reason}` : ''}</div>
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: C.body, cursor: disabled ? 'default' : 'pointer' }}>
        <input type="checkbox" checked={enabled} disabled={disabled} onChange={(e) => onToggle(e.target.checked)} />
        Enabled
      </label>
      <input type="number" min={1} max={999} value={priority} disabled={disabled} onChange={(e) => onPriority(Number(e.target.value))}
        style={{ width: 60, fontFamily: mono, fontSize: 12, color: C.body, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: '5px 8px' }} />
      <Badge origin={origin} />
    </div>
  );
}

function TimingRow({ band, rule }: { band: string; rule: string }) {
  return (
    <div style={{ display: 'flex', gap: 12, padding: '8px 10px', background: C.soft, borderRadius: 10 }}>
      <span style={{ fontSize: 12, fontWeight: 700, color: C.ink, minWidth: 140, flexShrink: 0 }}>{band}</span>
      <span style={{ fontSize: 12, color: C.subtle, lineHeight: 1.5 }}>{rule}</span>
    </div>
  );
}

function ThresholdField({ label, value, origin, disabled, min, max, step, onChange }: {
  label: string; value: number; origin: Origin; disabled?: boolean; min: number; max: number; step: number; onChange: (v: number) => void;
}) {
  return (
    <div style={{ opacity: disabled ? 0.6 : 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
        <label style={{ fontSize: 11, color: C.label, textTransform: 'capitalize' }}>{label}</label>
        <Badge origin={origin} />
      </div>
      <input type="number" min={min} max={max} step={step} value={value} disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{ width: '100%', fontFamily: mono, fontSize: 12, color: C.body, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: '7px 10px' }} />
    </div>
  );
}

function AddPlatformChip({ onAdd }: { onAdd: (v: string) => void }) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const commit = () => { const v = draft.trim(); if (v) onAdd(v); setDraft(''); setAdding(false); };
  if (!adding) {
    return (
      <button type="button" onClick={() => setAdding(true)}
        style={{ fontSize: 11, fontWeight: 700, color: C.accent, background: 'transparent', border: `1px dashed ${C.accent}`, borderRadius: 999, padding: '3px 9px', cursor: 'pointer' }}>
        + Add platform
      </button>
    );
  }
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      <input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setAdding(false); setDraft(''); } }}
        placeholder="e.g. Tableau" style={{ flex: 1, minWidth: 0, fontSize: 11, color: C.body, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: '5px 8px' }} />
      <button type="button" onClick={commit} style={{ cursor: 'pointer', fontSize: 11, fontWeight: 700, color: '#fff', background: C.accent, border: 'none', borderRadius: 8, padding: '5px 10px' }}>Add</button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Item 10 — immutable version history. List view is the sparse overrides (cheap); clicking a
// version fetches its full effective ruleset on demand — the "re-derive exactly as it was made"
// detail an auditor asking "why this, in June?" actually needs.
function VersionHistoryPanel({ versions, loaded, detail, detailLoading, onOpenDetail, onCloseDetail }: {
  versions: import('./useRuleConfig').RulesetVersionSummary[];
  loaded: boolean;
  detail: import('./useRuleConfig').RulesetVersionDetail | null;
  detailLoading: number | null;
  onOpenDetail: (version: number) => void;
  onCloseDetail: () => void;
}) {
  return (
    <SectionCard title="Version history" subtitle="Immutable — every publish adds a row here and never edits or removes an earlier one. Full effective ruleset available per version.">
      {!loaded ? (
        <div style={{ fontSize: 12, color: C.subtle }}>Loading…</div>
      ) : versions.length === 0 ? (
        <div style={{ fontSize: 12, color: C.subtle }}>Nothing published yet — this tenant is still on the unmodified default ruleset (v1).</div>
      ) : (
        <div style={{ display: 'grid', gap: 6 }}>
          {versions.map((v) => {
            const changeCount = countOverrides(v.overrides);
            return (
              <div key={v.version} style={{ border: `1px solid ${C.border}`, borderRadius: 10, background: C.soft }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px 12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontFamily: mono, fontSize: 12.5, fontWeight: 800, color: C.ink }}>v{v.version}</span>
                    <span style={{ fontSize: 11, color: C.subtle }}>
                      {changeCount} change{changeCount === 1 ? '' : 's'} · parent {v.parent_version ?? '—'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 11, color: C.subtle }}>
                      published {new Date(v.published_at).toLocaleString()} by {v.published_by?.name || v.published_by?.email || `user #${v.published_by?.id}`}
                    </span>
                    <button onClick={() => onOpenDetail(v.version)} style={{ fontSize: 11, fontWeight: 700, color: C.accent, background: 'transparent', border: `1px solid ${C.accent}`, borderRadius: 999, padding: '3px 10px', cursor: 'pointer' }}>
                      {detailLoading === v.version ? 'Loading…' : 'View full ruleset'}
                    </button>
                  </div>
                </div>
                {v.proposed_by && (
                  <div style={{ fontSize: 11, color: C.label, padding: '0 12px 8px' }}>
                    proposed by {v.proposed_by.name || v.proposed_by.email || `user #${v.proposed_by.id}`}{v.proposed_at ? ` · ${new Date(v.proposed_at).toLocaleString()}` : ''}
                  </div>
                )}
                {detail && detail.version === v.version && (
                  <div style={{ borderTop: `1px solid ${C.border}`, padding: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: C.label, textTransform: 'uppercase' }}>Full effective ruleset at v{v.version}</span>
                      <button onClick={onCloseDetail} style={{ fontSize: 11, color: C.subtle, background: 'transparent', border: 'none', cursor: 'pointer' }}>Close</button>
                    </div>
                    <pre style={{ margin: 0, maxHeight: 260, overflow: 'auto', fontSize: 11, fontFamily: mono, color: C.body, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: 10 }}>
                      {JSON.stringify(detail.ruleset, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </SectionCard>
  );
}
